import { useQuery } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

type OneOnOneDataExportsParams = {
  cohortId: number;
  startTS: number;
  endTS: number;
};

const generateOneOnOneDataExportsReport = async ({ cohortId, endTS, startTS }: OneOnOneDataExportsParams) => {
  try {
    const url = endpoints.dataExports.dataExportsReport;
    const response = await sendRequest({
      url,
      method: 'POST',
      credentials: 'include',
      body: {
        reportType: 'OOO_INSTANCE_REPORT',
        cohortId,
        baseFilter: {
          startTS,
          endTS,
        },
        rowGroupCols: [],
        pivotCols: [],
        pivotMode: false,
        filterModel: {},
        limit: null,
        sortModel: [],
      },
    });
    return response.entity;
  } catch (e) {
    console.error('Error failed to fetch 1:1 data export', e);
    throw e;
  }
};

export const useGenerateOneOnOneDataExportsReport = ({
  cohortId,
  endTS,
  startTS,
  enabled,
}: OneOnOneDataExportsParams & { enabled?: boolean }) => {
  const toast = useToast();

  return useQuery({
    queryKey: [REACT_QUERY_KEYS.dataExports.generateOneOnOneDataExportsReport, cohortId, startTS, endTS],
    queryFn: () => generateOneOnOneDataExportsReport({ cohortId, startTS, endTS }),
    enabled,
    // As we're rendering the same component with different cohorts, we want to refetch the data when the cohortId changes
    // Not when the component mounts
    // As it will make 2 requests, one on mount and one when the cohortId changes
    refetchOnMount: false,
    onError: () => {
      toast({
        title: 'Failed to generate 1:1 report, please try again',
        status: 'error',
      });
    },
  });
};
